import React, { useState, useEffect } from "react";
import Header from "../../components/Header/Header";
import CommonPieChart from "../../components/common/Chart/CommonPieChart";
import { useNavigate } from "react-router-dom";
import {
  useGetListSegmentionQuery,
  useGetListBusinessLocationQuery,
  useGetPerformanceQuery,
} from "../../services/apiSlice";
import { FormSelect } from "../../components/common/Select/FormSelect";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { useTranslation } from "react-i18next";
import Button from "../../components/common/Button/Button";

const colors = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#2ECC71",
  "#D81B60",
  "#3498DB",
  "#E91E63",
  "#9C27B0",
  "#673AB7",
  "#3F51B5",
  "#2196F3",
  "#00BCD4",
  "#009688",
  "#8BC34A",
  "#CDDC39",
  "#FFC107",
];

const Performance = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState({
    selectedLocation: "",
    selectedSegment: "",
    page: 1,
    limit: 10,
    hasMore: true,
  });

  const { data: segmentDataList, isLoading: isLoadingSegmentData } =
    useGetListSegmentionQuery({
      limit: state.limit,
      page: state.page,
    });

  const { data: locationListData, isLoading: isLoadingLocationListData } =
    useGetListBusinessLocationQuery();

  const { data: performanceData, isLoading: isLoadingPerformanceData } =
    useGetPerformanceQuery(
      {
        location_id: state.selectedLocation || "",
        segmentation_id: state.selectedSegment || "",
      },
      {
        skip: !state.selectedSegment,
        refetchOnMountOrArgChange: true,
      }
    );

  const locationOptions = [
    { value: "", label: t("performan.allLocation") },
    ...(locationListData?.data || [])
      .slice()
      .sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : a.address.toLowerCase();
        const nameB = b.name ? b.name.toLowerCase() : b.address.toLowerCase();
        return nameA.localeCompare(nameB);
      })
      .map((location) => ({
        value: location.id,
        label: location.name || location.address || location.country || "",
      })),
  ];

  const segmentOptions = [
    ...(segmentDataList?.data || []).map((segment) => ({
      value: segment.id,
      label: segment.name,
    })),
  ];

  useEffect(() => {
    if (
      segmentDataList?.data &&
      segmentDataList.data.length > 0 &&
      !state.selectedSegment
    ) {
      const ageOption = segmentDataList.data.find(
        (seg) => seg.name.toLowerCase() === "age"
      );
      if (ageOption) {
        setState((prev) => ({ ...prev, selectedSegment: ageOption.id }));
      }
    }
  }, [segmentDataList, state.selectedSegment]);

  const handleLocationChange = (value) => {
    setState((prev) => ({ ...prev, selectedLocation: value }));
  };

  const handleSegmentChange = (value) => {
    setState((prev) => ({ ...prev, selectedSegment: value }));
  };

  const chartData = (() => {
    if (!performanceData?.data?.segmentations || !state.selectedSegment) {
      return { data: [], labels: [], colors: colors.slice(0, 4) };
    }

    const activeSegmentation = performanceData.data.segmentations.find(
      (seg) => seg.segmentation_id === state.selectedSegment
    );

    if (!activeSegmentation?.statistics) {
      return { data: [], labels: [], colors: colors.slice(0, 4) };
    }

    return {
      data: activeSegmentation.statistics.map((stat) => stat.percentage || 0),
      labels: activeSegmentation.statistics.map(
        (stat) =>
          stat.title || stat.value || stat.option || t("performan.unKnown")
      ),
      colors: colors.slice(0, activeSegmentation.statistics.length),
      options: activeSegmentation.statistics.map((stat) => ({
        option_id: stat.option_id,
      })),
      type: activeSegmentation.type,
    };
  })();

  const handleSegmentClick = (clickedData) => {
    const { index } = clickedData || {};

    if (index !== undefined && chartData.data[index] !== undefined) {
      const clickedOption = chartData.options[index] || {};
      const clickedType = chartData.type || {};

      navigate(`/performan/agent`, {
        state: {
          dataChartProps: {
            segmentId: state.selectedSegment,
            locationId: state.selectedLocation,
            optionId: clickedOption,
            type: clickedType,
            chartData: chartData,
          },
        },
      });
    }
  };

  const hasData =
    chartData.data.length > 0 && chartData.data.some((val) => val > 0);

  return (
    <main className="overflow-hidden mx-auto w-full bg-neutral-50 max-w-[480px]">
      <LoadingCommon
        statusLoading={
          isLoadingLocationListData ||
          isLoadingPerformanceData ||
          isLoadingSegmentData
        }
      />
      <div className="w-full min-h-[920px]">
        <div className="fixed top-0 left-0 w-full bg-white z-50 flex justify-center items-center">
          <Header
            title={t("performan.performance")}
            classNameMenuIcon={"bg-white"}
            className={"max-w-[480px]"}
            onMenuClick={() => navigate("/performan/chat")}
          />
        </div>
        <div className="h-16"></div>
        <section className="flex flex-col flex-1 justify-between items-center p-3.5 w-full">
          {performanceData?.data.total_participants <
          performanceData?.data.number_participants_required ? (
            <article className="flex flex-col gap-8 items-center p-3 mx-auto max-w-none bg-white rounded-xl  max-md:p-2.5 max-md:w-full max-md:max-w-[991px] max-sm:p-2 max-sm:max-w-screen-sm">
              <h2 className="self-stretch text-lg font-bold text-zinc-800 max-md:text-base max-sm:text-sm">
                Insufficient Responses
              </h2>
              <p className="self-stretch text-sm tracking-wide text-neutral-400 max-md:text-sm max-sm:text-xs">
                You must collect enough mystery shopper responses from running
                Quests to view customer segmentation.
              </p>

              <div className="flex flex-col justify-center p-2.5 mt-6 w-full rounded-xl">
                <p className="self-stretch text-sm tracking-wide text-right text-neutral-400 max-md:text-sm max-sm:text-xs">
                  {performanceData?.data?.total_participants} / {20} mystery
                  shoppers
                </p>
                <div className="flex  gap-1 items-center w-full">
                  <div className="flex flex-col flex-1 shrink self-stretch my-auto rounded-2xl basis-0 min-w-[240px]">
                    <div className="flex flex-col items-start rounded-2xl bg-stone-200">
                      <div className="flex flex-col items-start w-44 max-w-full rounded-2xl">
                        <div
                          className="flex shrink-0 h-2 bg-[#3F54DF] rounded-2xl"
                          style={{
                            width: `${
                              (performanceData?.data?.total_participants / 20) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch my-auto text-xl font-semibold leading-none text-black">
                    <span className="text-sm font-bold leading-6 text-zinc-800">
                      {performanceData?.data?.total_participants}
                    </span>
                    <span className="text-sm font-bold leading-6 text-neutral-500">
                      /{20}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate("/ondemand")}
                text={"Run Quest"}
                className={`p-5 w-60 text-sm font-bold text-center text-white bg-indigo-600 rounded-[999px] max-md:p-4 max-md:w-full max-md:text-sm max-sm:p-3.5 max-sm:text-xs hover:bg-indigo-700 transition-colors `}
              />
            </article>
          ) : (
            <article className="flex flex-col justify-center p-3 max-w-full bg-white rounded-xl text-zinc-800 w-[396px]">
              <header className="flex gap-3 items-center w-full text-lg font-semibold whitespace-nowrap">
                <h2 className="gap-1.5 self-stretch my-auto">
                  {t("performan.summary")}
                </h2>
              </header>
              <p className="flex-1 shrink gap-2.5 self-stretch px-2 py-2.5 mt-4 w-full text-sm font-medium tracking-wide bg-yellow-50 rounded-xl basis-0">
                {performanceData?.data?.summary || t("performan.noSummaryData")}
              </p>
              <div className="mt-3 w-full">
                <FormSelect
                  value={state.selectedLocation}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  options={locationOptions}
                  defaultValue={t("performan.allLocation")}
                  className="mt-0 bg-stone-100 text-blue-600 text-sm font-medium"
                />
              </div>
              <div className="mt-3 w-full">
                <FormSelect
                  value={state.selectedSegment}
                  onChange={(e) => handleSegmentChange(e.target.value)}
                  options={segmentOptions}
                  className="mt-0 bg-stone-100 text-blue-600 text-sm font-medium"
                />
              </div>
              <div className="w-full mt-3 flex justify-center items-center">
                {hasData ? (
                  <CommonPieChart
                    data={chartData.data}
                    labels={chartData.labels}
                    colors={chartData.colors}
                    width="100%"
                    showLabels={true}
                    onSegmentClick={(clickedData) =>
                      handleSegmentClick(clickedData)
                    }
                  />
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <p className="text-zinc-400">
                      {t("performan.noDataAvailable")}
                    </p>
                  </div>
                )}
              </div>
            </article>
          )}
        </section>
      </div>
    </main>
  );
};

export default Performance;
